import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "counterFields",
    "declineFields",
    "quantityField",
    "quantityInput",
    "deliveryDateField",
    "deliveryDateInput",
    "paymentField",
    "paymentInput",
    "messageInput",
    "declineInput",
    "submitButton",
    "dateErrorAlert",
    "acceptOption"
  ]

  static values = {
    datePassed: Boolean
  }

  connect() {
    console.log("Seller decision controller connected")

    // If date has passed, set up for counter offer mode
    if (this.datePassedValue) {
      this.setupForDatePassed()
    } else {
      this.updateFormAction('accept')
    }
  }

  setupForDatePassed() {
    // Counter is already selected via the view, just make sure fields are enabled
    if (this.hasMessageInputTarget) {
      this.messageInputTarget.disabled = false
    }
    this.updateFormAction('accept')
  }

  handleDecisionChange(event) {
    const decision = event.target.value

    // Hide all conditional sections
    if (this.hasCounterFieldsTarget) {
      this.counterFieldsTarget.classList.add('hidden')
      this.disableAllCounterFields()
    }

    if (this.hasDeclineFieldsTarget) {
      this.declineFieldsTarget.classList.add('hidden')
      if (this.hasDeclineInputTarget) {
        this.declineInputTarget.disabled = true
      }
    }

    // Show relevant section based on choice
    if (decision === 'counter') {
      this.counterFieldsTarget.classList.remove('hidden')
      if (this.hasMessageInputTarget) {
        this.messageInputTarget.disabled = false
      }
      this.updateFormAction('accept')
    } else if (decision === 'decline') {
      this.declineFieldsTarget.classList.remove('hidden')
      if (this.hasDeclineInputTarget) {
        this.declineInputTarget.disabled = false
      }
      // Get decline path from data attribute
      const declinePath = event.target.dataset.sellerDecisionDeclinePath
      this.updateFormAction('decline', declinePath)
    } else {
      // Accept as-is
      this.updateFormAction('accept')
    }
  }

  toggleField(event) {
    const checkbox = event.target
    const fieldName = checkbox.name.replace('adjust_', '')

    // Map field names to their targets
    const fieldMap = {
      'quantity': ['quantityField', 'quantityInput'],
      'delivery_date': ['deliveryDateField', 'deliveryDateInput'],
      'payment_method': ['paymentField', 'paymentInput']
    }

    const [fieldTarget, inputTarget] = fieldMap[fieldName]

    if (this.hasTarget(fieldTarget)) {
      const field = this[`${fieldTarget}Target`]
      const input = this[`${inputTarget}Target`]

      if (checkbox.checked) {
        field.classList.remove('hidden')
        input.disabled = false

        // If delivery date checkbox is checked and we have a date passed error,
        // add listener to hide error when valid date selected
        if (fieldName === 'delivery_date' && this.datePassedValue) {
          input.addEventListener('change', this.checkDeliveryDate.bind(this))
        }
      } else {
        field.classList.add('hidden')
        input.disabled = true

        // If unchecking delivery date and date was passed, show the error again
        if (fieldName === 'delivery_date' && this.datePassedValue) {
          this.showDateError()
        }
      }
    }
  }

  checkDeliveryDate(event) {
    const selectedDate = new Date(event.target.value)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    if (selectedDate >= tomorrow) {
      // Valid date selected - hide error
      this.hideDateError()
    } else {
      // Still invalid - show error
      this.showDateError()
    }
  }

  hideDateError() {
    if (this.hasDateErrorAlertTarget) {
      this.dateErrorAlertTarget.classList.add('hidden')
    }
  }

  showDateError() {
    if (this.hasDateErrorAlertTarget) {
      this.dateErrorAlertTarget.classList.remove('hidden')
    }
  }

  disableAllCounterFields() {
    const inputs = [
      'quantityInput',
      'deliveryDateInput',
      'paymentInput',
      'messageInput'
    ]

    inputs.forEach(inputName => {
      if (this.hasTarget(inputName)) {
        this[`${inputName}Target`].disabled = true
      }
    })
  }

  updateFormAction(decision, customPath = null) {
    const form = this.element

    if (decision === 'decline' && customPath) {
      form.action = customPath
    } else {
      // Keep the default accept action
      // Form action is already set to accept_seller_order_path
    }
  }

  hasTarget(targetName) {
    return this.targets.has(targetName)
  }
}
